import { I18n } from '@affine/i18n';

import type {
  PdfSigningJobStatus,
  SignatureValidationState,
  SignedPdfVersionInfo,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';

const JOB_STATUS_KEYS = {
  queued: 'com.streamlain.pdf.signature.digital.job.queued',
  running: 'com.streamlain.pdf.signature.digital.job.running',
  finalizing: 'com.streamlain.pdf.signature.digital.job.finalizing',
  completed: 'com.streamlain.pdf.signature.digital.job.completed',
  failed: 'com.streamlain.pdf.signature.digital.job.failed',
  cancelled: 'com.streamlain.pdf.signature.digital.job.cancelled',
} as const satisfies Record<PdfSigningJobStatus, string>;

const VALIDATION_STATE_KEYS = {
  valid: 'com.streamlain.pdf.signature.validation.valid',
  invalid: 'com.streamlain.pdf.signature.validation.invalid',
  unknown: 'com.streamlain.pdf.signature.validation.unknown',
  untrusted: 'com.streamlain.pdf.signature.validation.untrusted',
  expired: 'com.streamlain.pdf.signature.validation.expired',
  revoked: 'com.streamlain.pdf.signature.validation.revoked',
  visual_only: 'com.streamlain.pdf.signature.validation.visual_only',
} as const satisfies Record<SignatureValidationState, string>;

/** How the check of a signed copy reads at a glance. */
export type SignatureValidationTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';

const VALIDATION_STATE_TONES = {
  valid: 'success',
  invalid: 'danger',
  revoked: 'danger',
  expired: 'warning',
  untrusted: 'warning',
  unknown: 'neutral',
  visual_only: 'neutral',
} as const satisfies Record<SignatureValidationState, SignatureValidationTone>;

/** Where a signing job stands, in the reader's words. */
export function getSigningJobStatusLabel(status: PdfSigningJobStatus) {
  return I18n.t(JOB_STATUS_KEYS[status]);
}

/** What the check of a signed copy found. */
export function getSignatureValidationStateLabel(
  state: SignatureValidationState
) {
  return I18n.t(VALIDATION_STATE_KEYS[state]);
}

export function getSignatureValidationTone(
  state: SignatureValidationState
): SignatureValidationTone {
  return VALIDATION_STATE_TONES[state];
}

export function formatPadesLevelLabel(
  level: SignedPdfVersionInfo['padesLevel']
) {
  switch (level) {
    case 'B':
      return 'PAdES-B';
    case 'T':
      return 'PAdES-B-T';
    case 'LT':
      return 'PAdES-B-LT';
    case 'LTA':
      return 'PAdES-B-LTA';
  }
}

/** A point in time as the reader writes it; other notes stay as they are. */
function formatMoment(value: string) {
  const time = Date.parse(value);
  return Number.isNaN(time) ? value : new Date(time).toLocaleString();
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value) return value;
  }
  return null;
}

/** The file name of a signed copy; a copy without one goes by its time. */
export function formatSignedPdfVersionName(
  version: Pick<SignedPdfVersionInfo, 'fileName' | 'createdAt'>
) {
  return version.fileName || formatMoment(version.createdAt);
}

/** Whose signature a copy carries. */
export function getSignedPdfVersionSigner(
  version: Pick<SignedPdfVersionInfo, 'signingProfileName' | 'providerName'>
) {
  return (
    version.signingProfileName ||
    version.providerName ||
    I18n.t('com.streamlain.pdf.signature.versions.detail.fallbackName')
  );
}

/** When a copy was signed, as the reader writes it. */
export function formatSignedPdfVersionSigningTime(
  version: Pick<SignedPdfVersionInfo, 'createdAt' | 'validationSummary'>
) {
  const summary = version.validationSummary ?? {};
  return formatMoment(
    firstText(summary.signingTime, summary.signedAt) ?? version.createdAt
  );
}

export interface SignedPdfVersionDetail {
  label: string;
  value: string;
}

/**
 * The facts of a signed copy, one per line: who signed, when, to which
 * standard, what was signed and with which certificate.
 */
export function describeSignedPdfVersion(
  version: SignedPdfVersionInfo
): SignedPdfVersionDetail[] {
  const summary = version.validationSummary ?? {};
  const timestampTime = firstText(summary.timestampTime, summary.timestamp);
  const longTermNote = firstText(summary.ltv, summary.lta);
  const validatedAt = firstText(summary.validatedAt, summary.validationTime);
  const failureReason = firstText(summary.failureReason, summary.error);
  const present = I18n.t(
    'com.streamlain.pdf.signature.versions.detail.present'
  );

  const details: SignedPdfVersionDetail[] = [
    {
      label: I18n.t('com.streamlain.pdf.signature.versions.detail.signedBy'),
      value: getSignedPdfVersionSigner(version),
    },
    {
      label: I18n.t('com.streamlain.pdf.signature.versions.detail.signedAt'),
      value: formatSignedPdfVersionSigningTime(version),
    },
    {
      label: I18n.t('com.streamlain.pdf.signature.versions.detail.standard'),
      value: formatPadesLevelLabel(version.padesLevel),
    },
  ];
  const add = (key: string, value: string | null | undefined) => {
    if (value) details.push({ label: I18n.t(key), value });
  };

  add(
    'com.streamlain.pdf.signature.versions.detail.timestamp',
    timestampTime
      ? formatMoment(timestampTime)
      : summary.timestamped === true
        ? present
        : null
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.longTerm',
    longTermNote ??
      (summary.ltv === true || summary.lta === true ? present : null)
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.content',
    version.annotationMode === 'flattened' ||
      version.sourceKind === 'flattened_annotations'
      ? I18n.t('com.streamlain.pdf.signature.versions.detail.contentAnnotated')
      : I18n.t('com.streamlain.pdf.signature.versions.detail.contentOriginal')
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.provider',
    version.providerName
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.subject',
    version.certificateSubject
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.issuer',
    version.certificateIssuer
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.fingerprint',
    version.certificateFingerprint
  );
  add(
    'com.streamlain.pdf.signature.versions.detail.validatedAt',
    validatedAt ? formatMoment(validatedAt) : null
  );
  add('com.streamlain.pdf.signature.versions.detail.reason', failureReason);
  return details;
}

/**
 * What the check did not cover. A copy can be mathematically intact while
 * nobody has checked whether its certificate's issuer is trusted.
 */
export function getSignedPdfVersionTrustNote(
  version: Pick<SignedPdfVersionInfo, 'validationSummary'>
): string | null {
  return version.validationSummary?.trustChecked === false
    ? I18n.t('com.streamlain.pdf.signature.versions.trustNotChecked')
    : null;
}
